import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { CurrencyInput } from "./CurrencyInput";
import "./plan-management.css";

type Plan = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: "draft" | "active" | "discontinued";
  visibility: "public" | "internal" | "custom";
  version: number;
  is_trial: boolean;
  trial_days: number | null;
  currency_code: "EUR" | "BRL";
  price_cents: number | null;
  billing_interval: "monthly" | "yearly" | "one_time" | null;
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
  sort_order: number;
};
type PlanVersion = {
  id: string;
  version: number;
  snapshot: Plan;
  created_at: string;
};
const featureOptions = [
  ["crm", "CRM comercial"],
  ["stock", "Gestão de stock"],
  ["website", "Website do stand"],
  ["chat", "Chat e distribuição comercial"],
  ["marketplaces", "Publicação em marketplaces"],
  ["integrations", "Integrações externas"],
] as const;
const limitOptions = [
  ["users", "Utilizadores"],
  ["vehicles", "Viaturas"],
  ["ads", "Anúncios ativos"],
  ["storage_gb", "Armazenamento (GB)"],
  ["chat_agents", "Comerciais no chat"],
  ["integrations", "Integrações"],
] as const;
const empty: Omit<Plan, "id" | "version"> = {
  code: "",
  name: "",
  description: "",
  status: "draft",
  visibility: "public",
  is_trial: false,
  trial_days: 7,
  currency_code: "EUR",
  price_cents: 0,
  billing_interval: "monthly",
  features: {},
  limits: {},
  sort_order: 100,
};
const money = (plan: Plan) =>
  new Intl.NumberFormat(plan.currency_code === "BRL" ? "pt-BR" : "pt-PT", {
    style: "currency",
    currency: plan.currency_code,
  }).format((plan.price_cents ?? 0) / 100);

export function PlanManagement({ canManage }: { canManage: boolean }) {
  const [plans, setPlans] = useState<Plan[]>([]),
    [loading, setLoading] = useState(true),
    [editing, setEditing] = useState<Plan | null | undefined>(undefined),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    const { data, error } = await supabase
      .from("plans")
      .select(
        "id,code,name,description,status,visibility,version,is_trial,trial_days,currency_code,price_cents,billing_interval,features,limits,sort_order",
      )
      .order("sort_order")
      .order("name");
    if (error) setMessage(error.message);
    setPlans((data ?? []) as Plan[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  const counts = useMemo(
    () => ({
      active: plans.filter((p) => p.status === "active").length,
      draft: plans.filter((p) => p.status === "draft").length,
      discontinued: plans.filter((p) => p.status === "discontinued").length,
    }),
    [plans],
  );
  return (
    <section className="plans-workspace">
      <div className="plans-heading">
        <div>
          <p className="eyebrow">Catálogo comercial</p>
          <p>
            Configure o que cada plano oferece. As subscrições guardam sempre as
            condições contratadas.
          </p>
        </div>
        {canManage && (
          <button
            className="primary-button compact"
            onClick={() => setEditing(null)}
          >
            Criar plano
          </button>
        )}
      </div>
      {message && (
        <button className="notice" role="status" aria-live="polite" onClick={() => setMessage(null)}>
          {message} ×
        </button>
      )}
      <div className="plan-summary">
        <article>
          <small>Ativos</small>
          <strong>{counts.active}</strong>
        </article>
        <article>
          <small>Rascunhos</small>
          <strong>{counts.draft}</strong>
        </article>
        <article>
          <small>Descontinuados</small>
          <strong>{counts.discontinued}</strong>
        </article>
      </div>
      {loading ? (
        <div className="empty-state">A carregar planos…</div>
      ) : plans.length === 0 ? (
        <div className="empty-state">
          <strong>Ainda não existem planos.</strong>
          <span>Crie o primeiro plano comercial para começar.</span>
        </div>
      ) : (
        <div className="plan-grid">
          {plans.map((plan) => (
            <button
              key={plan.id}
              className={`plan-card ${plan.status}`}
              onClick={() => canManage && setEditing(plan)}
            >
              <span className="plan-state">
                {plan.status === "active"
                  ? "Ativo"
                  : plan.status === "draft"
                    ? "Rascunho"
                    : "Descontinuado"}
              </span>
              <small>
                {plan.visibility === "public"
                  ? "Público"
                  : plan.visibility === "internal"
                    ? "Interno"
                    : "Personalizado"}{" "}
                · v{plan.version}
              </small>
              <h2>{plan.name}</h2>
              <p>{plan.description || "Sem descrição comercial."}</p>
              <strong>
                {money(plan)}{" "}
                <small>
                  {plan.billing_interval === "monthly"
                    ? "/ mês"
                    : plan.billing_interval === "yearly"
                      ? "/ ano"
                      : ""}
                </small>
              </strong>
              <footer>
                <span>
                  {Object.values(plan.features ?? {}).filter(Boolean).length}{" "}
                  funcionalidades
                </span>
                <span>{Object.keys(plan.limits ?? {}).length} limites</span>
                {plan.trial_days && (
                  <span>{plan.trial_days} dias de teste</span>
                )}
              </footer>
            </button>
          ))}
        </div>
      )}
      {editing !== undefined && (
        <PlanDialog
          plan={editing}
          onClose={() => setEditing(undefined)}
          onSaved={async () => {
            setEditing(undefined);
            setMessage("Plano guardado e nova versão registada.");
            await load();
          }}
        />
      )}
    </section>
  );
}

function PlanDialog({
  plan,
  onClose,
  onSaved,
}: {
  plan: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const base = plan ?? empty,
    [features, setFeatures] = useState<Record<string, boolean>>(
      base.features ?? {},
    ),
    [limits, setLimits] = useState<Record<string, number | null>>(
      base.limits ?? {},
    ),
    [versions, setVersions] = useState<PlanVersion[]>([]),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!plan) return;
    void supabase
      .from("plan_versions")
      .select("id,version,snapshot,created_at")
      .eq("plan_id", plan.id)
      .order("version", { ascending: false })
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setVersions((data ?? []) as PlanVersion[]);
      });
  }, [plan]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const f = new FormData(event.currentTarget),
      payload = {
        code: String(f.get("code"))
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
        name: String(f.get("name")).trim(),
        description: String(f.get("description") || "") || null,
        status: String(f.get("status")),
        visibility: String(f.get("visibility")),
        is_trial: f.get("is_trial") === "on",
        trial_days: f.get("trial_days") ? Number(f.get("trial_days")) : null,
        currency_code: String(f.get("currency_code")),
        price_cents: Math.round(Number(f.get("price") || 0) * 100),
        billing_interval: String(f.get("billing_interval")),
        features,
        limits,
        sort_order: Number(f.get("sort_order") || 100),
      };
    const query = plan
      ? supabase.from("plans").update(payload).eq("id", plan.id)
      : supabase.from("plans").insert(payload);
    const { error } = await query;
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    onSaved();
  };
  return (
    <div className="modal-backdrop">
      <section className="plan-dialog" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">Construtor de planos</p>
            <h2>{plan ? "Editar plano" : "Criar plano"}</h2>
            <p>
              {plan
                ? `A próxima gravação criará a versão ${plan.version + 1}. As subscrições existentes conservam a versão contratada.`
                : "Defina as condições comerciais e operacionais."}
            </p>
          </div>
          <button className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label>
              <span>Nome *</span>
              <input
                name="name"
                defaultValue={base.name}
                required
                minLength={2}
              />
            </label>
            <label>
              <span>Código interno *</span>
              <small className="field-help">Identificador técnico estável, sem espaços, usado nas regras do plano; não é mostrado ao cliente.</small>
              <input
                name="code"
                defaultValue={base.code}
                required
                placeholder="ex.: profissional"
              />
            </label>
            <label className="wide">
              <span>Descrição comercial</span>
              <textarea
                name="description"
                defaultValue={base.description ?? ""}
                rows={2}
              />
            </label>
            <label>
              <span>Estado</span>
              <small className="field-help">Ativo pode ser contratado; rascunho ainda está em preparação; descontinuado preserva contratos antigos.</small>
              <select name="status" defaultValue={base.status}>
                <option value="draft">Rascunho</option>
                <option value="active">Ativo</option>
                <option value="discontinued">Descontinuado</option>
              </select>
            </label>
            <label>
              <span>Visibilidade</span>
              <small className="field-help">Público aparece no site; interno é usado apenas pela equipa; personalizado depende de proposta.</small>
              <select name="visibility" defaultValue={base.visibility}>
                <option value="public">Público</option>
                <option value="internal">Interno</option>
                <option value="custom">Personalizado</option>
              </select>
            </label>
            <label>
              <span>Preço</span>
              <CurrencyInput
                currencyCode={base.currency_code}
                name="price"
                type="number"
                min="0"
                step="0.01"
                defaultValue={(base.price_cents ?? 0) / 100}
              />
            </label>
            <label>
              <span>Moeda</span>
              <select name="currency_code" defaultValue={base.currency_code}>
                <option value="EUR">EUR — Euro</option>
                <option value="BRL">BRL — Real</option>
              </select>
            </label>
            <label>
              <span>Periodicidade</span>
              <small className="field-help">Frequência usada para cobrança e renovação deste plano.</small>
              <select
                name="billing_interval"
                defaultValue={base.billing_interval ?? "monthly"}
              >
                <option value="monthly">Mensal</option>
                <option value="yearly">Anual</option>
                <option value="one_time">Pagamento único</option>
              </select>
            </label>
            <label>
              <span>Dias de degustação</span>
              <small className="field-help">Período gratuito antes da primeira cobrança; vazio significa sem teste.</small>
              <input
                name="trial_days"
                type="number"
                min="1"
                max="90"
                defaultValue={base.trial_days ?? ""}
                placeholder="Sem degustação"
              />
            </label>
            <label>
              <span>Ordem no catálogo</span>
              <small className="field-help">Números menores aparecem primeiro na apresentação dos planos.</small>
              <input
                name="sort_order"
                type="number"
                min="0"
                defaultValue={base.sort_order}
              />
            </label>
            <label className="check-line">
              <input
                name="is_trial"
                type="checkbox"
                defaultChecked={base.is_trial}
              />
              <span>Este é um plano exclusivamente experimental</span>
            </label>
          </div>
          <fieldset>
            <legend>Funcionalidades incluídas</legend>
            <div className="option-grid">
              {featureOptions.map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={!!features[key]}
                    onChange={(e) =>
                      setFeatures({ ...features, [key]: e.target.checked })
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Limites automáticos</legend>
            <div className="limits-grid">
              {limitOptions.map(([key, label]) => (
                <label key={key}>
                  <span>{label}</span>
                  <input
                    type="number"
                    min="0"
                    value={limits[key] ?? ""}
                    placeholder="Ilimitado"
                    onChange={(e) =>
                      setLimits({
                        ...limits,
                        [key]:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </label>
              ))}
            </div>
            <small>Deixe vazio para permitir utilização ilimitada.</small>
          </fieldset>
          {plan && (
            <fieldset className="plan-history">
              <legend>Histórico imutável</legend>
              <p>
                Cada gravação cria uma nova versão. Alterar este plano nunca
                modifica as condições já contratadas por um stand.
              </p>
              {versions.length === 0 ? (
                <small>A carregar versões…</small>
              ) : (
                versions.map((item) => (
                  <article key={item.id}>
                    <strong>Versão {item.version}</strong>
                    <span>
                      {new Date(item.created_at).toLocaleString("pt-PT")}
                    </span>
                    <em>
                      {new Intl.NumberFormat(
                        item.snapshot.currency_code === "BRL"
                          ? "pt-BR"
                          : "pt-PT",
                        {
                          style: "currency",
                          currency: item.snapshot.currency_code,
                        },
                      ).format((item.snapshot.price_cents ?? 0) / 100)}{" "}
                      ·{" "}
                      {item.snapshot.status === "active"
                        ? "Ativa"
                        : item.snapshot.status === "draft"
                          ? "Rascunho"
                          : "Descontinuada"}
                    </em>
                  </article>
                ))
              )}
            </fieldset>
          )}
          {error && <p className="form-error">{error}</p>}
          <footer>
            <button type="button" className="text-button" onClick={onClose}>
              Cancelar
            </button>
            <button className="primary-button compact" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
